import {
  PlayCircle,
  Globe,
  Server,
  Hexagon,
  Database,
  HardDrive,
  Settings,
  ShieldCheck,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import { brand } from '@/config/brand';

/**
 * Service catalog taxonomy — ported from the legacy @console ServiceDashboard
 * `SERVICE_CATEGORIES`, re-skinned for @vnetwork/ui v2 (lucide icons). Categories
 * group providers; a provisioned service maps to a category by its provider
 * scope (`categoryOfScope`).
 */

export type ProductStatus = 'active' | 'beta' | 'coming_soon';

export interface ServiceProduct {
  name: string; // animal key (legacy)
  displayName: string;
  scope: string; // provider_scope
  description: string;
  features: string[];
  status: ProductStatus;
  /**
   * Platform features that aren't provisioned per-service (no provider scope)
   * link straight into their own console instead of a catalog landing page.
   */
  consoleRoute?: string;
  /**
   * Animal codename of the provider a first-time activation provisions on, when
   * the product is self-serve. Present → the catalog CTA activates the service
   * (backend mints the tenant) instead of a contact-sales inquiry. The catalog
   * identity (name/scope) and the activation provider can differ — Multi-CDN is
   * catalogued as `owl` but activates on `raven` (vEdge, the consolidation edge).
   */
  activateProvider?: string;
}

export interface ServiceCategory {
  key: string;
  title: string;
  icon: LucideIcon;
  description: string;
  products: ServiceProduct[];
  /** Optional highlight pill shown on the sidebar entry (e.g. "Popular"). When set,
   *  it takes the badge slot instead of the provisioned-count number. */
  badge?: string;
  /** Dedicated category landing page (e.g. `/services/compute`). When set, the
   *  sidebar entry opens this hub instead of the query-filtered dashboard
   *  (`/services?category=<key>`). */
  landingRoute?: string;
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    key: 'streaming',
    title: 'Media & Streaming',
    icon: PlayCircle,
    description: 'Live playout, transcoding and video delivery.',
    landingRoute: '/services/streaming',
    products: [
      {
        name: 'parrot',
        displayName: 'FastChannel',
        scope: 'Internal.FastChannel',
        description: 'Free ad-supported streaming TV with scheduling, playout and EPG.',
        features: ['Scheduling', 'Playout', 'VOD Integration', 'EPG'],
        status: 'active',
      },
      {
        name: 'penguin',
        displayName: 'Live Streaming',
        // Penguin/LMS live services carry Internal.HLSLowLatency on the backend.
        scope: 'Internal.HLSLowLatency',
        description: 'Enterprise live streaming (LMS) with live transcoding, DRM and multi-CDN delivery.',
        features: ['Live Transcoding', 'DRM', 'Multi-CDN', 'Low Latency'],
        status: 'active',
        // Self-serve: provisions on the `penguin` provider (LMS/FlashCone).
        // On success → /services/live/:id
        activateProvider: 'penguin',
      },
      {
        name: 'lens',
        displayName: 'Media Service',
        scope: 'External.SwiftCoder',
        description: 'On-demand video (VOD) transcoding, packaging and adaptive-bitrate streaming.',
        features: ['VOD', 'Transcoding', 'Packaging', 'Adaptive Bitrate'],
        status: 'active',
        // Self-serve: provisions on the `lens` provider (VMS/SwiftCoder).
        // On success → /services/vod/:id
        activateProvider: 'lens',
      },
      {
        name: 'peacock',
        displayName: 'On-Demand Transcoding',
        scope: 'Internal.Transcoding',
        description: 'On-demand transcoding to multiple formats, resolutions and thumbnails.',
        features: ['Multiple Formats', 'Adaptive Bitrate', 'Thumbnails', 'Watermarking'],
        status: 'coming_soon',
      },
      {
        name: 'pangolin',
        displayName: 'AES Gateway',
        scope: 'Internal.Aes',
        description: 'HLS AES-128 key gateway for VOD and live, with metrics and logs.',
        features: ['HLS AES-128', 'VOD + LIVE', 'Live logs', 'Metrics'],
        status: 'beta',
        // Provisioned per service → routed via serviceRoute(scope, id) to
        // /services/aes/:serviceId (no static consoleRoute).
      },
    ],
  },
  {
    key: 'cdn',
    title: 'Networking & CDN',
    icon: Globe,
    description: 'Edge delivery, caching, WAF and TLS for your domains.',
    landingRoute: '/services/cdn',
    products: [
      {
        name: 'owl',
        displayName: 'Multi-CDN Orchestration',
        scope: 'External.Mlytics',
        description: 'Orchestrate delivery across multiple CDN backends with WAF and TLS.',
        features: ['Domains', 'Analytics', 'WAF', 'SSL/TLS'],
        status: 'active',
        activateProvider: 'raven',
      },
    ],
  },
  {
    key: 'compute',
    title: 'Compute',
    icon: Server,
    description: 'Virtual machines and bare-metal compute.',
    landingRoute: '/services/compute',
    products: [
      {
        name: 'panda',
        displayName: 'Cloud Compute',
        scope: 'Internal.Opennebula',
        description: 'Virtual machines with networking, load balancers and backups.',
        features: ['VMs', 'Networking', 'Load Balancers', 'Snapshots'],
        status: 'active',
        // Self-serve: provisions on the `panda` provider (OpenNebula/Techev Cloud).
        activateProvider: 'panda',
      },
      {
        name: 'bear',
        displayName: 'Enterprise Compute',
        scope: 'Internal.VMware',
        description: 'Dedicated virtual machines with high availability and monitoring.',
        features: ['VMs', 'Monitoring', 'High Availability'],
        status: 'active',
      },
      {
        name: 'cheetah',
        displayName: 'Serverless Functions',
        scope: 'Internal.Functions',
        description: 'Run code without servers, with auto-scaling and pay-per-use.',
        features: ['Auto-scaling', 'Pay-per-use', 'API Gateway'],
        status: 'coming_soon',
      },
    ],
  },
  {
    key: 'kubernetes',
    title: 'Kubernetes',
    icon: Hexagon,
    description: 'Managed clusters and container tooling.',
    landingRoute: '/services/kubernetes',
    products: [
      {
        name: 'wolf',
        displayName: 'Managed Kubernetes',
        scope: 'Internal.Opennebula.Kubernetes',
        description: 'Managed clusters with node pools, ingress and auto-scaling.',
        features: ['Clusters', 'Node Pools', 'Ingress', 'Auto-scaling'],
        status: 'active',
        // Self-serve: provisions on the `wolf` provider (Managed Kubernetes).
        activateProvider: 'wolf',
      },
      {
        name: 'husky',
        displayName: 'Container Registry',
        scope: 'Internal.ContainerRegistry',
        description: 'Private container image storage with vulnerability scanning.',
        features: ['Image Storage', 'Scanning', 'Access Control'],
        status: 'coming_soon',
      },
    ],
  },
  {
    key: 'databases',
    title: 'Databases',
    icon: Database,
    description: 'Managed relational, cache and document databases.',
    landingRoute: '/services/databases',
    products: [
      {
        name: 'elephant',
        displayName: 'Database as a Service',
        scope: 'Internal.Opennebula.Database',
        description: 'Managed MySQL, PostgreSQL, MariaDB and Redis with auto backups.',
        features: ['RDS', 'Redis', 'Auto Backups', 'Monitoring'],
        status: 'active',
      },
    ],
  },
  {
    key: 'storage',
    title: 'Storage',
    icon: HardDrive,
    description: 'S3-compatible object storage.',
    landingRoute: '/services/storage',
    products: [
      {
        name: 'walrus',
        displayName: 'Object Storage',
        scope: 'Internal.Minio',
        description: 'S3-compatible object buckets with access keys and versioning.',
        features: ['Buckets', 'Access Keys', 'Versioning'],
        status: 'beta',
        // No consoleRoute: like every non-WAAP product it goes through the catalog
        // detail → contact-sales inquiry when not provisioned. A provisioned Minio
        // service still opens its console via serviceRoute (/services/oss/:id).
      },
      // Block storage is already delivered as Compute Volumes (SSD volumes +
      // snapshots), so no standalone "Block Storage" catalog product here.
    ],
  },
  {
    key: 'observability',
    title: 'Observability',
    icon: Activity,
    description: 'Uptime monitoring, latency tracking and incident alerting.',
    landingRoute: '/services/observability',
    products: [
      {
        name: 'heron',
        displayName: 'Healthcheck & Alerting',
        scope: 'Internal.Healthcheck',
        description: 'Monitor endpoints, ports and certificates with multi-channel alerting.',
        features: ['Uptime', 'Latency', 'TLS expiry', 'Alerting'],
        status: 'beta',
        consoleRoute: '/services/healthcheck',
      },
    ],
  },
  {
    key: 'management',
    title: 'Management',
    icon: Settings,
    description: 'Identity, organizations, billing and API keys.',
    landingRoute: '/services/management',
    products: [
      {
        name: 'griffin',
        displayName: 'Organizations',
        scope: 'Management.Organizations',
        description: 'Projects, teams and resources with fine-grained permissions.',
        features: ['Projects', 'Teams', 'Permissions'],
        status: 'active',
        consoleRoute: '/services/organizations',
      },
      {
        name: 'unicorn',
        displayName: 'Usage & Cost Management',
        scope: 'Management.Billing',
        description: 'Invoices, payment methods and detailed usage reports.',
        features: ['Invoices', 'Payments', 'Usage'],
        status: 'coming_soon',
      },
    ],
  },
  {
    key: 'security',
    title: 'Security',
    icon: ShieldCheck,
    description: 'WAF, certificates and secrets.',
    badge: 'Popular',
    landingRoute: '/services/security',
    products: [
      {
        name: 'cobra',
        displayName: 'Web Application & API Protection',
        scope: 'Security.WAAP',
        description:
          'Web Application & API Protection — AI-WAF, bot management, rate limiting and API security for your websites.',
        features: ['AI-WAF', 'Bot Management', 'Rate Limiting', 'API Protection'],
        status: 'active',
        // Self-serve: a first-time activation provisions on the standalone
        // `cobra` provider (Conversant WAAP), separate from the eagle CDN.
        activateProvider: 'cobra',
        // Routes to the WAAP landing gate (mirrors healthcheck): it shows the
        // activation hero when the org has no WAAP service, else redirects into
        // the console — instead of the generic catalog locked-preview.
        consoleRoute: '/services/waap',
      },
      {
        name: 'lion',
        displayName: 'Web Application Firewall',
        scope: 'Security.WAF',
        description: 'DDoS protection with OWASP rules and bot management.',
        features: ['DDoS', 'OWASP', 'Bot Management'],
        status: 'coming_soon',
      },
      {
        name: 'tiger',
        displayName: 'SSL/TLS Certificates',
        scope: 'Security.Certificates',
        description: 'Issue, renew and manage certificates with auto-renewal.',
        features: ['Auto-renewal', 'Wildcard', 'ACME'],
        status: 'beta',
        consoleRoute: '/services/certinvent',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Top-level nav groups
// ---------------------------------------------------------------------------

/** A labelled cluster of categories — the uppercase headings in the sidebar. */
export interface ServiceGroup {
  heading: string;
  /** Category keys under this group, in display order. */
  categoryKeys: string[];
}

/**
 * The four top-level groups the services sidebar clusters categories under
 * (Cloudflare-style section headings). Every catalog category belongs to exactly
 * one group; order here drives the sidebar order.
 */
export const SERVICE_GROUPS: ServiceGroup[] = [
  { heading: 'Protect', categoryKeys: ['security'] },
  { heading: 'Infrastructure', categoryKeys: ['compute', 'kubernetes', 'databases', 'storage'] },
  { heading: 'Delivery', categoryKeys: ['streaming', 'cdn'] },
  { heading: 'Observe & Manage', categoryKeys: ['observability', 'management'] },
];

// ---------------------------------------------------------------------------
// Scope → category, route, icon
// ---------------------------------------------------------------------------

const SCOPE_CATEGORY: Record<string, string> = {
  // streaming
  'Internal.FastChannel': 'streaming',
  'External.SwiftCoder': 'streaming', // Lens / VMS — Cloud Encoding console
  // Penguin / LMS — Live Transcoding console. Backend emits Internal.HLSLowLatency
  // as the live provider scope; External.SwiftCoder.LMS is kept as a legacy alias.
  'Internal.HLSLowLatency': 'streaming',
  'External.SwiftCoder.LMS': 'streaming',
  'Internal.Transcoding': 'streaming',
  'Internal.VOD': 'streaming',
  'Internal.Aes': 'streaming', // Pangolin AES Gateway — grouped with Media & Streaming
  // cdn (vEdge/EdgeNext/Raven/Hawk all ride the Manticore CDN UI)
  'External.SwiftFederation': 'cdn',
  'External.Raven': 'cdn',
  'External.Hawk': 'cdn',
  'External.Bunny.CDN': 'cdn',
  'External.Conversant.CDN': 'cdn',
  'External.Aliyun.CDN': 'cdn',
  'External.Mlytics': 'cdn',
  // compute
  'Internal.Opennebula': 'compute',
  'Internal.VMware': 'compute',
  'Internal.VMware.Private': 'compute',
  // kubernetes
  'Internal.Opennebula.Kubernetes': 'kubernetes',
  // databases
  'Internal.Opennebula.Database': 'databases',
  // storage
  'Internal.Minio': 'storage',
  'Internal.Minio.Managed': 'storage',
  // security — WAAP (Cobra/Conversant). The provisioned service's provider
  // legacy_scope is `External.Conversant.WAAP` (the catalog card uses the
  // synthetic `Security.WAAP`); without this entry every categoryOfScope
  // consumer (Overview by-product, sidebar nav tree, …) buckets WAAP as 'other'.
  'External.Conversant.WAAP': 'security',
};

/** Map a provider scope to a category key (or "other"). */
export function categoryOfScope(scope: string | undefined): string {
  if (!scope) return 'other';
  return SCOPE_CATEGORY[scope] ?? 'other';
}

// ── Brand product allowlist ────────────────────────────────────────────────
// A whitelabel brand may offer only a subset of products. `brand.services` lists
// the enabled product codenames (ServiceProduct.name); undefined = all products.

/** Whether the active brand offers this product (by codename). */
export function isProductEnabled(name: string): boolean {
  return !brand.services || brand.services.includes(name);
}

/** True when the brand offers at least one product in this category. */
export function isCategoryEnabled(cat: ServiceCategory): boolean {
  return cat.products.some((p) => isProductEnabled(p.name));
}

/** True when the brand offers the category a provisioned scope belongs to.
 *  Unknown / "other" scopes are never hidden (a provisioned service always shows). */
export function isScopeCategoryEnabled(scope: string | undefined): boolean {
  const cat = categoryByKey(categoryOfScope(scope));
  return cat ? isCategoryEnabled(cat) : true;
}

/**
 * SERVICE_CATEGORIES filtered to the active brand: categories with no enabled
 * product are dropped, and each remaining category keeps only enabled products.
 * Returns the original array unchanged when the brand allows everything.
 */
/** Whether a product is shown to the active brand: not coming-soon and allowlisted. */
export function isProductVisible(p: ServiceProduct): boolean {
  return p.status !== 'coming_soon' && isProductEnabled(p.name);
}

export function brandServiceCategories(): ServiceCategory[] {
  return SERVICE_CATEGORIES.map((c) => ({
    ...c,
    products: c.products.filter(isProductVisible),
  })).filter((c) => c.products.length > 0);
}

/** A single category with only the products visible to the active brand — for
 *  the per-category landing pages (e.g. /services/compute). */
export function brandCategoryByKey(key: string): ServiceCategory | undefined {
  const c = categoryByKey(key);
  return c ? { ...c, products: c.products.filter(isProductVisible) } : undefined;
}

const CDN_SCOPES = new Set([
  'External.SwiftFederation',
  'External.Raven',
  'External.Hawk',
  'External.Bunny.CDN',
  'External.Conversant.CDN',
  'External.Aliyun.CDN',
  'External.Mlytics',
]);

/**
 * Customer-facing canonical pick order when an org holds multiple CDN provider
 * services. vEdge (Raven) is the consolidation destination so it wins; the rest
 * fall back by delivery preference, then alphabetically (handled by the caller).
 */
export const CANONICAL_CDN_SCOPE_PRIORITY = [
  'External.Raven', // vEdge — primary
  'External.SwiftFederation', // Manticore / Eagle
  'External.Hawk', // EdgeNext
  'External.Bunny.CDN', // Bunny.net
  'External.Conversant.CDN',
  'External.Aliyun.CDN',
  'External.Mlytics',
] as const;

/**
 * Where a provisioned service opens. Only the surfaces the console actually
 * implements return a route; everything else is provisioned-but-not-yet-wired.
 */
export function serviceRoute(scope: string | undefined, serviceId: string): string | null {
  if (!scope) return null;
  // Owl/Mlytics opens its own WAF/CDN console (dedicated /api/v3/waf module),
  // not the shared Manticore CDN console. Must precede the CDN_SCOPES check
  // below — `External.Mlytics` is still listed there for catalog grouping.
  if (scope === 'External.Mlytics') return `/services/waf/${serviceId}`;
  // Cobra/Conversant WAAP — its own standalone console.
  if (scope === 'External.Conversant.WAAP') return `/services/waap/${serviceId}`;
  if (scope === 'Internal.FastChannel') return `/services/fastchannel/${serviceId}`;
  if (scope === 'Internal.VMware') return `/services/compute/${serviceId}`;
  if (scope === 'Internal.Opennebula') return `/services/compute/${serviceId}`;
  if (scope === 'Internal.Opennebula.Kubernetes') return `/services/kubernetes/${serviceId}`;
  if (scope === 'Internal.Opennebula.Database') return `/services/database/${serviceId}`;
  if (scope === 'Internal.Aes') return `/services/aes/${serviceId}`;
  // Lens / VMS — Cloud Encoding (VOD) console.
  if (scope === 'External.SwiftCoder') return `/services/vod/${serviceId}`;
  // Penguin/LMS live services carry Internal.HLSLowLatency on the backend;
  // External.SwiftCoder.LMS retained as a legacy alias for the same console.
  if (scope === 'Internal.HLSLowLatency' || scope === 'External.SwiftCoder.LMS')
    return `/services/live/${serviceId}`;
  if (scope === 'Internal.Minio' || scope === 'Internal.Minio.Managed') return `/services/oss/${serviceId}`;
  if (CDN_SCOPES.has(scope)) return `/services/cdn/${serviceId}`;
  return null;
}

const CATEGORY_BY_KEY: Record<string, ServiceCategory> = Object.fromEntries(
  SERVICE_CATEGORIES.map((c) => [c.key, c]),
);

export function categoryByKey(key: string): ServiceCategory | undefined {
  return CATEGORY_BY_KEY[key];
}

/**
 * Where a service console / catalog page's "Back" should land. When the owning
 * category has its own landing page (Compute, Security), return there so the
 * user goes back to the category they came from; otherwise fall back to the
 * all-services dashboard. Prevents every console from dumping the user on "All
 * services" regardless of where they entered.
 */
export function categoryBackTarget(categoryKey: string | undefined): { path: string; label: string } {
  const cat = categoryKey ? CATEGORY_BY_KEY[categoryKey] : undefined;
  return cat?.landingRoute
    ? { path: cat.landingRoute, label: `Back to ${cat.title}` }
    : { path: '/services', label: 'Back to Services' };
}

const PRODUCT_BY_SCOPE: Record<string, ServiceProduct> = Object.fromEntries(
  SERVICE_CATEGORIES.flatMap((c) => c.products.map((p) => [p.scope, p])),
);
// Penguin/LMS live services appear under two backend scopes in the wild
// (`Internal.HLSLowLatency` and `External.SwiftCoder.LMS`) — alias both to the
// same Live Media Service product so either provisioned card shows its details.
PRODUCT_BY_SCOPE['External.SwiftCoder.LMS'] = PRODUCT_BY_SCOPE['Internal.HLSLowLatency'];

/** Catalog product for a provider scope — lets a provisioned card reuse the
 * product's description + features so active services look as complete as
 * catalog ones. Multi-CDN umbrellas resolve via the canonical `External.Mlytics`. */
export function productByScope(scope: string | undefined): ServiceProduct | undefined {
  return scope ? PRODUCT_BY_SCOPE[scope] : undefined;
}

/**
 * URL of a catalog product's landing page. Segments are dot-free (category key +
 * animal name) on purpose — provider scopes contain dots (e.g. `Internal.Transcoding`),
 * and a dotted last path segment makes the dev server's history fallback treat it
 * as a static file and 404 on deep-link / hard refresh.
 */
export function catalogPath(categoryKey: string, productName: string): string {
  return `/services/catalog/${categoryKey}/${productName}`;
}

/** Resolve a catalog product from its (category key, animal name) URL segments. */
export function findCatalogProduct(
  categoryKey: string | undefined,
  productName: string | undefined,
): { product: ServiceProduct; category: ServiceCategory } | null {
  const category = categoryKey ? CATEGORY_BY_KEY[categoryKey] : undefined;
  if (!category) return null;
  const product = category.products.find((p) => p.name === productName);
  return product ? { product, category } : null;
}

/** Icon for a service, by its category. */
export function iconForScope(scope: string | undefined): LucideIcon {
  return CATEGORY_BY_KEY[categoryOfScope(scope)]?.icon ?? Globe;
}
